import {
  Anchor,
  Box,
  StackProps,
  Divider,
  Stack,
  Text,
  Button,
} from '@mantine/core';
import { Trans, useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  PhoneField,
  PrivacyDataModal,
  CheckboxField,
  TextField,
  OptField,
  FormButton,
} from 'components/shared';
import { STATIC_URLS } from 'consts';
import { useExtFormsOtp } from 'api/extForms';
import { FORM_ERROR } from 'components/shared/Form/types';
import { useIsMdBreakpoint } from 'hooks';
import { Page } from './Page';

export type SendStepProps = StackProps & {
  offerShareId: string;
  signMethod: string;
  prevStep: () => void;
};

export const OtpStep: React.FC<SendStepProps> = ({
  offerShareId,
  signMethod,
  prevStep,
  ...stackProps
}) => {
  const isMdBreakpoint = useIsMdBreakpoint();
  const mutation = useExtFormsOtp(offerShareId);
  const { t } = useTranslation(['common', 'user']);
  const {
    formState: { errors },
    watch,
  } = useFormContext();

  const codeIsProvided = watch('code')?.length > 6;

  // isValid from useFormState not working correct with OptField component
  const errorsWithoutRoot = Object.keys(errors).filter(
    key => key !== FORM_ERROR,
  );
  const hasErrors = errorsWithoutRoot.length > 0 && !codeIsProvided;

  const [isPrivacyDataModalOpen, setIsPrivacyDataModalOpen] = useState(false);

  const acceptTermsLabel = (
    <Trans
      i18nKey="user:acceptTerms"
      components={{
        a: <Anchor href={STATIC_URLS.termsAndConditions} target="_blank" />,
      }}
    />
  );

  const personalDataInfo = (
    <Text fz="xs" fw={400}>
      {t('user:personalDataInfo')}{' '}
      <Anchor
        component="button"
        sx={theme => ({ fontSize: theme.fontSizes.xs })}
        onClick={() => setIsPrivacyDataModalOpen(true)}
      >
        {t('common:more')}
      </Anchor>
    </Text>
  );

  return (
    <Page>
      <Box
        sx={{
          display: 'grid',
          gridTemplateRows: '1fr auto',
          overflow: 'hidden',
          height: '100%',
        }}
      >
        <Stack pt={24} spacing="lg" sx={{ overflow: 'auto' }} {...stackProps}>
          <Box>
            <Stack spacing={0}>
              <TextField
                required
                name="name"
                maw={333}
                label={t('common:name')}
              />
              <TextField
                required
                name="surname"
                maw={333}
                label={t('common:surname')}
              />
              <TextField
                required={signMethod !== 'SMS'}
                name="email"
                maw={333}
                label={t('common:email')}
              />
              <Box maw={333}>
                <PhoneField
                  required={signMethod === 'SMS'}
                  name="phone"
                  label={`${t('common:phoneNumber')}`}
                />
              </Box>

              <CheckboxField required name="terms" label={acceptTermsLabel} />
            </Stack>
            {personalDataInfo}
          </Box>
          <Divider />
          <OptField
            method={signMethod}
            sendFields={['name', 'surname', 'email', 'phone']}
            mutation={mutation}
          />
        </Stack>

        <Box
          my={16}
          sx={theme => ({
            display: 'flex',
            columnGap: theme.spacing.sm,
            justifyContent: isMdBreakpoint ? 'flex-end' : 'center',
          })}
        >
          <Button variant="default" onClick={() => prevStep()}>
            {t('common:back')}
          </Button>
          <FormButton type="submit" disabled={hasErrors}>
            {t('common:send')}
          </FormButton>
        </Box>
      </Box>
      <PrivacyDataModal
        isOpen={isPrivacyDataModalOpen}
        onClose={() => setIsPrivacyDataModalOpen(false)}
        onSubmit={() => setIsPrivacyDataModalOpen(false)}
      />
    </Page>
  );
};
